import React, { useState } from 'react';
import { Routes, Route, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

import ManageUsers from './ManageUsers';
import ManageClasses from './ManageClasses';
import ManageSubjects from './ManageSubjects';
import ManageFees from './ManageFees';
import NoticeBoard from './NoticeBoard';
import Timetable from './Timetable';
import ViewComplaints from './ViewComplaints';
import ViewRatings from './ViewRatings';
import ClassReports from './ClassReports';

const Dashboard = () => {
  const { user, logoutUser } = useAuth();
  const navigate = useNavigate();
  const [activeMenu, setActiveMenu] = useState('dashboard');
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const menuItems = [
    { id: 'dashboard', label: 'Dashboard', icon: '🏠' },
    { id: 'users', label: 'Manage Users', icon: '👥' },
    { id: 'classes', label: 'Classes', icon: '🏫' },
    { id: 'subjects', label: 'Subjects', icon: '📚' },
    { id: 'fees', label: 'Fees', icon: '💰' },
    { id: 'timetable', label: 'Timetable', icon: '📅' },
    { id: 'notices', label: 'Notice Board', icon: '📢' },
    { id: 'complaints', label: 'Complaints', icon: '📝' },
    { id: 'ratings', label: 'Teacher Ratings', icon: '⭐' },
    { id: 'reports', label: 'Class Reports', icon: '📈' },
  ];

  const handleMenu = (id) => {
    setActiveMenu(id);
    navigate(`/admin/${id}`);
    setSidebarOpen(false);
  };

  const handleLogout = () => {
    logoutUser();
    navigate('/login');
  };

  const isMobile = window.innerWidth <= 768;

  return (
    <div style={styles.container}>
      {/* Mobile Overlay */}
      {sidebarOpen && isMobile && (
        <div onClick={() => setSidebarOpen(false)} style={styles.overlay} />
      )}

      {/* Sidebar */}
      <div style={{
        ...styles.sidebar,
        left: isMobile ? (sidebarOpen ? '0' : '-260px') : '0',
        transition: 'left 0.3s ease',
        zIndex: 1000,
      }}>
        <div style={styles.sidebarHeader}>
          <img src="/logo.png" alt="logo" style={styles.logo} />
          <h3 style={styles.collegeName}>Stars College</h3>
          <p style={styles.campusName}>Tibbi Qaisrani Campus</p>
        </div>
        <div style={styles.userInfo}>
          <div style={styles.avatar}>
            {user?.name?.charAt(0).toUpperCase()}
          </div>
          <div>
            <p style={styles.userName}>{user?.name}</p>
            <p style={styles.userRole}>Administrator</p>
          </div>
        </div>
        <nav style={styles.nav}>
          {menuItems.map(item => (
            <button key={item.id}
              style={{ ...styles.navItem, ...(activeMenu === item.id ? styles.navItemActive : {}) }}
              onClick={() => handleMenu(item.id)}>
              <span style={styles.navIcon}>{item.icon}</span>
              {item.label}
            </button>
          ))}
        </nav>
        <button onClick={handleLogout} style={styles.logoutBtn}>
          🚪 Logout
        </button>
      </div>

      {/* Main Content */}
      <div style={{
        ...styles.mainContent,
        marginLeft: isMobile ? '0' : '250px',
      }}>
        {/* Top Bar */}
        <div style={styles.topBar}>
          {isMobile && (
            <button onClick={() => setSidebarOpen(!sidebarOpen)} style={styles.hamburger}>
              ☰
            </button>
          )}
          <h2 style={styles.pageTitle}>Admin Panel</h2>
          <p style={styles.welcome}>Welcome, {user?.name}!</p>
        </div>

        <div style={styles.content}>
          <Routes>
            <Route path="/" element={<AdminHome />} />
            <Route path="/dashboard" element={<AdminHome />} />
            <Route path="/users" element={<ManageUsers />} />
            <Route path="/classes" element={<ManageClasses />} />
            <Route path="/subjects" element={<ManageSubjects />} />
            <Route path="/fees" element={<ManageFees />} />
            <Route path="/timetable" element={<Timetable />} />
            <Route path="/notices" element={<NoticeBoard />} />
            <Route path="/complaints" element={<ViewComplaints />} />
            <Route path="/ratings" element={<ViewRatings />} />
            <Route path="/reports" element={<ClassReports />} />
          </Routes>
        </div>
      </div>
    </div>
  );
};

const AdminHome = () => (
  <div>
    <h3 style={{ color: '#1a3a5c', marginBottom: '20px' }}>
      Welcome to Stars College Admin Panel
    </h3>
    <div style={styles.statsGrid}>
      {[
        { icon: '👥', label: 'Manage Students & Teachers' },
        { icon: '📚', label: 'Manage Subjects & Classes' },
        { icon: '💰', label: 'Manage Fees' },
        { icon: '📢', label: 'Notice Board' },
      ].map((item, i) => (
        <div key={i} style={styles.statCard}>
          <h2 style={styles.statNumber}>{item.icon}</h2>
          <p style={styles.statLabel}>{item.label}</p>
        </div>
      ))}
    </div>
  </div>
);

const styles = {
  container: { display: 'flex', minHeight: '100vh', fontFamily: 'Arial, sans-serif' },
  overlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', zIndex: 999 },
  sidebar: {
    width: '250px',
    background: 'linear-gradient(180deg, #1a3a5c 0%, #0d2a4a 100%)',
    display: 'flex',
    flexDirection: 'column',
    position: 'fixed',
    height: '100vh',
    overflowY: 'auto',
  },
  sidebarHeader: { textAlign: 'center', padding: '20px 15px', borderBottom: '1px solid rgba(255,255,255,0.1)' },
  logo: { width: '60px', height: '60px', borderRadius: '50%' },
  collegeName: { color: 'white', fontSize: '16px', margin: '8px 0 2px' },
  campusName: { color: 'rgba(255,255,255,0.6)', fontSize: '11px', margin: 0 },
  userInfo: { display: 'flex', alignItems: 'center', gap: '10px', padding: '15px', borderBottom: '1px solid rgba(255,255,255,0.1)' },
  avatar: { width: '36px', height: '36px', borderRadius: '50%', background: '#0d6efd', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold', fontSize: '16px' },
  userName: { color: 'white', margin: 0, fontSize: '13px', fontWeight: 'bold' },
  userRole: { color: 'rgba(255,255,255,0.6)', margin: 0, fontSize: '11px' },
  nav: { flex: 1, padding: '10px 0' },
  navItem: { width: '100%', padding: '12px 20px', background: 'transparent', border: 'none', color: 'rgba(255,255,255,0.8)', textAlign: 'left', cursor: 'pointer', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '10px' },
  navItemActive: { background: 'rgba(13,110,253,0.4)', color: 'white', borderLeft: '3px solid #0d6efd' },
  navIcon: { fontSize: '16px' },
  logoutBtn: { margin: '10px 15px 20px', padding: '10px', background: 'rgba(220,53,69,0.3)', border: '1px solid rgba(220,53,69,0.5)', color: 'white', borderRadius: '8px', cursor: 'pointer', fontSize: '13px' },
  mainContent: { flex: 1, background: '#f5f7fa', minHeight: '100vh' },
  topBar: { background: 'white', padding: '15px 25px', borderBottom: '1px solid #e0e0e0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  hamburger: { background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#1a3a5c', marginRight: '10px' },
  pageTitle: { margin: 0, color: '#1a3a5c', fontSize: '20px' },
  welcome: { margin: 0, color: '#666', fontSize: '14px' },
  content: { padding: '25px' },
  statsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '20px' },
  statCard: { background: 'white', borderRadius: '12px', padding: '25px', textAlign: 'center', boxShadow: '0 2px 10px rgba(0,0,0,0.08)' },
  statNumber: { fontSize: '40px', margin: '0 0 10px' },
  statLabel: { color: '#666', fontSize: '14px', margin: 0 },
};

export default Dashboard;